import { create } from 'zustand'

/**
 * Transient, non-persisted UI notifications (import results, edit-operation
 * outcomes, etc.). Deliberately a separate store from `activity-store.ts`
 * — notifications are ephemeral view state, not application/document data,
 * and have no business being in the persisted UI-preferences slice either.
 */
export type NotificationVariant = 'success' | 'error' | 'warning'

export interface Notification {
  readonly id: string
  readonly variant: NotificationVariant
  readonly title: string
  readonly description?: string
}

interface NotificationStoreState {
  notifications: Notification[]
  notify: (notification: Omit<Notification, 'id'>) => void
  dismiss: (id: string) => void
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `notification-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const useNotificationStore = create<NotificationStoreState>((set) => ({
  notifications: [],
  notify: (notification) =>
    set((state) => ({
      notifications: [...state.notifications, { ...notification, id: createId() }],
    })),
  dismiss: (id) =>
    set((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) })),
}))
